import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { fmtNum, fmtRiyal, toEnglishDigits } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime, combineDateTime } from "@/utils/date";
import { buildDriverWhatsAppUrl } from "@/utils/whatsappDriver";
import { buildCapacityMap, loadCachedCapacityMap, saveCachedCapacityMap } from "@/utils/seats";
import { translateLocation } from "@/utils/locationI18n";
import {
  ArrowLeft,
  CalendarBlank,
  Clock,
  MapPin,
  Car as CarIcon,
  User as UserIcon,
  WhatsappLogo,
  Plus,
  IdentificationCard,
} from "@phosphor-icons/react";

const CONFIRMED_STYLE = "text-[#2E7D32] border-[#2E7D32]/50 bg-[#2E7D32]/10";

function BookingCard({ b, navigate, capacityMap, t, lang }) {
  const iso = b.pickup_time || combineDateTime(b.trip_date, b.trip_time);
  const hasDriver = !!(b.driver_id || b.driver_name || b.external_driver_name);
  const driverName = b.driver_name || b.external_driver_name;
  const drv = {
    name: driverName,
    phone: b.driver_phone || b.external_driver_phone,
  };
  const waUrl = hasDriver ? buildDriverWhatsAppUrl(b, drv, { capacityMap }) : null;

  return (
    <div
      data-testid={`my-booking-${b.id}`}
      onClick={(e) => {
        if (e.target.closest("button, a")) return;
        navigate(`/booking/${b.id}`);
      }}
      className="fadeup rounded-2xl border border-primary/20 bg-card p-5 hover:border-primary/50 transition-all cursor-pointer"
    >
      {/* Header: number + status */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <IdentificationCard size={18} weight="duotone" className="text-primary shrink-0" />
          <span className="text-primary font-bold num truncate" dir="ltr">
            #{toEnglishDigits(b.booking_number || b.seq_number || b.internal_serial || "—")}
          </span>
        </div>
        <span className={`text-[11px] px-2.5 py-1 rounded-full border ${CONFIRMED_STYLE}`}>
          {t("myBookings.confirmed")}
        </span>
      </div>

      {/* Date + time */}
      <div className="flex items-center gap-3 text-sm text-white/80 mb-2">
        <span className="inline-flex items-center gap-1">
          <CalendarBlank size={13} className="text-primary/70" />
          <span className="num">{fmtDate(iso || b.trip_date)}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock size={13} className="text-primary/70" />
          <span className="num">{fmtTime(iso)}</span>
        </span>
      </div>

      {/* Route */}
      <div className="flex items-center gap-2 text-sm text-white/85 mb-2">
        <MapPin size={13} weight="fill" className="text-primary/70" />
        <span className="truncate">{translateLocation(b.pickup_location, lang) || "—"}</span>
        <ArrowLeft size={12} className="text-white/40 shrink-0" />
        <span className="truncate">{translateLocation(b.destination || b.dropoff_location, lang) || "—"}</span>
      </div>

      {/* Car + price */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-primary/10">
        <div className="flex items-center gap-2 text-sm text-white/75">
          <CarIcon size={14} weight="duotone" className="text-primary/70" />
          <span>{b.car_type_name || b.car_name_ar || b.car_type || "—"}</span>
          {b.passengers ? (
            <>
              <span className="text-white/40">·</span>
              <span className="num text-white/60 text-xs">
                {fmtNum(b.passengers)} {t("myBookings.passengers")}
              </span>
            </>
          ) : null}
        </div>
        <span className="text-primary font-bold num text-lg">
          {fmtRiyal(b.price || b.total_price)}
        </span>
      </div>

      {/* Driver row */}
      {hasDriver && (
        <div className="mt-3 pt-3 border-t border-primary/10">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-white/85 min-w-0">
              <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                <UserIcon size={14} weight="duotone" className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{driverName}</div>
                {b.plate_number && (
                  <div className="text-[10px] text-white/50 num" dir="ltr">
                    {b.plate_number}
                  </div>
                )}
              </div>
            </div>
            {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`my-booking-wa-${b.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/25 text-xs font-bold"
              >
                <WhatsappLogo size={13} weight="fill" /> {t("myBookings.waDriver")}
              </a>
            )}
          </div>
        </div>
      )}

      {!hasDriver && (
        <div className="mt-3 pt-3 border-t border-primary/10 text-white/50 text-xs italic">
          {t("myBookings.waitingDriver")}
        </div>
      )}
    </div>
  );
}

export default function MyBookings() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [bookings, setBookings] = useState([]);
  const [carsCapacity, setCarsCapacity] = useState(() => loadCachedCapacityMap());
  useEffect(() => {
    api.get("/cars/capacity")
      .catch(() => api.get("/cars"))
      .then((r) => {
        const m = buildCapacityMap(r.data);
        if (Object.keys(m).length > 0) { setCarsCapacity(m); saveCachedCapacityMap(m); }
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [statusChip, setStatusChip] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t("myBookings.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const statusCounts = useMemo(() => {
    const c = { all: bookings.length };
    ["confirmed", "completed", "cancelled", "rejected"].forEach((k) => {
      c[k] = bookings.filter((b) => b.status === k).length;
    });
    return c;
  }, [bookings]);

  const filtered = useMemo(() => {
    if (statusChip === "all") return bookings;
    return bookings.filter((b) => b.status === statusChip);
  }, [bookings, statusChip]);

  const statusChips = useMemo(
    () => [{ key: "all", label: t("common.all") }],
    [t],
  );

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="my-bookings-page">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t("myBookings.title")}</h1>
            <p className="text-white/50 text-sm mt-1">
              {t("myBookings.counterTemplate", {
                shown: fmtNum(filtered.length),
                total: fmtNum(bookings.length),
              })}
            </p>
          </div>
          <Link
            to="/new-booking"
            data-testid="my-bookings-new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold shadow-[0_10px_30px_-10px_rgba(212,175,55,0.5)]"
          >
            <Plus size={16} weight="bold" /> {t("myBookings.newBooking")}
          </Link>
        </div>

        {/* Status chips */}
        <div className="mb-5">
          <div className="text-primary font-bold text-sm mb-2">{t("myBookings.statusFilterLabel")}</div>
          <div dir={isEn ? "ltr" : "rtl"} className="flex flex-row items-center flex-wrap gap-2" data-testid="my-bookings-status-chips">
            {statusChips.map((c) => {
              const active = statusChip === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setStatusChip(c.key)}
                  data-testid={`my-chip-status-${c.key}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                    active
                      ? "bg-primary text-primary-foreground border-primary font-bold"
                      : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                  }`}
                >
                  <span>{c.label}</span>
                  <span
                    className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                    }`}
                  >
                    {fmtNum(statusCounts[c.key] || 0)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">{t("myBookings.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">
            {t("myBookings.empty")}{" "}
            <Link to="/new-booking" className="text-primary hover:underline">
              {t("myBookings.emptyCta")}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((b) => (
              <BookingCard key={b.id} b={b} navigate={navigate} capacityMap={carsCapacity} t={t} lang={i18n.language} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
